'use client';

import { type ReactNode, type RefObject, useLayoutEffect, useRef } from 'react';

import { useLatest } from 'react-use';

import { focusInPlace, isFocusDropped } from '@/lib/utils/dom';

function focusTargetIn(region: HTMLElement): HTMLElement | null {
  return (
    region.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6, [role="status"]') ??
    (region.firstElementChild as HTMLElement | null)
  );
}

/** A control that was focused and then removed or disabled took focus with it. */
function wasDropped(element: HTMLElement | null): boolean {
  return element !== null && (!element.isConnected || element.matches(':disabled'));
}

/**
 * Key one region per state. When a region leaves while holding focus, the next one sharing
 * `handoffRef` takes focus on its heading (or status, or first element) rather than leaving it on
 * the body, where a keyboard or screen-reader user starts over from the top.
 */
export function FocusHandoffRegion({
  handoffRef,
  focusTarget,
  children,
}: {
  handoffRef: RefObject<boolean>;
  /** Falls back to the first heading when this finds nothing. */
  focusTarget?: (region: HTMLElement) => HTMLElement | null | undefined;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);
  const focusTargetRef = useLatest(focusTarget);

  useLayoutEffect(
    function handOffFocus() {
      const region = ref.current;
      if (handoffRef.current && region && isFocusDropped()) {
        focusInPlace(focusTargetRef.current?.(region) ?? focusTargetIn(region));
      }
      handoffRef.current = false;
      // React runs this before it removes the region's nodes, so the focused one is still here.
      return function noteFocusLeaving() {
        if (!region) return;
        const holdsFocus =
          region.contains(document.activeElement) ||
          (isFocusDropped() && wasDropped(lastFocused.current));
        if (holdsFocus) handoffRef.current = true;
      };
    },
    [handoffRef, focusTargetRef],
  );

  return (
    <div
      ref={ref}
      className="contents"
      onFocus={function trackFocus(event) {
        lastFocused.current = event.target;
      }}
      onBlur={function forgetFocus(event) {
        const next = event.relatedTarget;
        if (next instanceof Node && !ref.current?.contains(next)) lastFocused.current = null;
      }}
    >
      {children}
    </div>
  );
}
